// CartV2 - investor-grade professional checkout.
// Uses shared pricing config. GST 5% Alberta. Founding customer discount.
// Migrated to Tailwind CSS + Novala tokens.

import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from '../../hooks/useQuery';
import { useTranslation } from "react-i18next";
import { getCountry, getPlan, getPayroll, getTaxLabel, getPromoMonths } from "../../config/countries";
import type { PlanId, PayrollTierId } from "../../config/countries";
import MarketingHeader from "../../components/MarketingHeader";
import MarketingFooter from "../../components/MarketingFooter";
import { CheckIcon } from './components/CheckIcon';
import { Stepper } from './components/Stepper';
import { PlanCard } from './components/PlanCard';
import { PayrollCard } from './components/PayrollCard';
import { AddPayrollButton } from './components/AddPayrollButton';
import { EmployeeAdjuster } from './components/EmployeeAdjuster';
import { OrderSummary } from './components/OrderSummary';

export default function CartV2() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const q = useQuery();
  const planId = (q.get("plan") || "essentials") as PlanId;
  const billing = q.get("billing") || "monthly";
  const payrollId = (q.get("payroll") || "none") as PayrollTierId | "none";
  const initEmp = parseInt(q.get("employees") || "5", 10);
  const [employeeCount, setEmployeeCount] = useState<number>(isNaN(initEmp) ? 5 : initEmp);

  const planName: string = ({ essentials: "Novala Essentials", premium: "Novala Premium", scale: "Novala Advanced" } as Record<string, string>)[planId] || "Novala Essentials";
  const planTagline: string = ({ essentials: t("addPayroll.tiers.core.tagline"), premium: t("addPayroll.tiers.premium.tagline"), scale: t("addPayroll.tiers.elite.tagline") } as Record<string, string>)[planId] || "";
  const planUsers = planId === "essentials" ? "Up to 5 users, plus your accountant" : planId === "premium" ? "Up to 25 users, plus your accountant" : "Unlimited users, plus your accountant";

  const country = getCountry();
  const planPricing = getPlan(planId);
  const payroll = payrollId !== "none" ? getPayroll(payrollId as PayrollTierId) : null;
  const payrollName: string = payroll ? (({ core: "Payroll Core", premium: "Payroll Premium", elite: "Payroll Elite" } as Record<string, string>)[payrollId] || "") : "";
  const payrollDesc: string = payroll ? (({ core: "Automated payroll, tax filing, direct deposit, year-end T4 forms.", premium: "Automated payroll, tax filing, direct deposit, time tracking, and HR document templates.", elite: "Automated payroll, tax filing, direct deposit, tax penalty protection, personal HR advisor, multi-location support." } as Record<string, string>)[payrollId] || "") : "";

  const planRegular = planPricing.regular;
  const planPromo = planPricing.promo;
  const payrollRegularBase = payroll ? payroll.regular : 0;
  const payrollPromoBase = payroll ? payroll.promo : 0;
  const perEmpCharge = payroll ? payroll.perEmployee * employeeCount : 0;
  const payrollTotal = payrollPromoBase + perEmpCharge;

  const subtotalRegular = planRegular + payrollRegularBase + perEmpCharge;
  const subtotalPromo = planPromo + payrollPromoBase + perEmpCharge;
  const savings = subtotalRegular - subtotalPromo;
  const gst = Math.round(subtotalPromo * country.tax.rate * 100) / 100;
  const total = Math.round((subtotalPromo + gst) * 100) / 100;

  const goCheckout = () => {
    const p = new URLSearchParams({ plan: planId, billing, payroll: payrollId, employees: String(employeeCount) });
    navigate("/checkout?" + p.toString());
  };
  const goChangePlan = () => { window.location.href = "https://www.getnovala.com/pricing"; };
  const goChangePayroll = () => navigate("/add-payroll-v2?plan=" + planId + "&billing=" + billing);

  return (
    <div className="min-h-screen bg-white font-sans">
      <MarketingHeader />

      <div className="text-center" style={{ background: "linear-gradient(90deg, #FEF3C7 0%, #FDE68A 100%)", padding: "10px 32px" }}>
        <span className="text-tiny font-extrabold" style={{ color: "#92400E", letterSpacing: "0.08em" }}>
          {t("common.checkout.founding")}
        </span>
      </div>

      <div className="bg-white text-center" style={{ padding: "32px 32px 96px" }}>
        <Stepper hasPayroll={!!payroll} t={t} />
      </div>

      <div className="mx-auto grid items-start gap-12" style={{ maxWidth: 1100, padding: "24px 32px 60px", gridTemplateColumns: "1.4fr 1fr" }}>

        <div>
          <div className="mb-6">
            <div className="text-hero font-extrabold text-ink" style={{ letterSpacing: "-0.02em" }}>{t("cart.yourOrder")}</div>
            <div className="mt-1 text-small text-sub">{t("cart.yourOrderSubtitle")}</div>
          </div>

          <PlanCard planName={planName} planTagline={planTagline} planUsers={planUsers} planPromo={planPromo} language={i18n.language} t={t} onChange={goChangePlan} />

          {payroll ? (
            <PayrollCard payrollName={payrollName} payrollDesc={payrollDesc} payrollTotal={payrollTotal} payrollPromoBase={payrollPromoBase} perEmployee={payroll.perEmployee} employeeCount={employeeCount} language={i18n.language} t={t} onChange={goChangePayroll} />
          ) : (
            <AddPayrollButton onClick={goChangePayroll} t={t} />
          )}

          {payroll && (
            <EmployeeAdjuster
              count={employeeCount}
              onDecrement={() => setEmployeeCount(Math.max(0, employeeCount - 1))}
              onIncrement={() => setEmployeeCount(Math.min(999, employeeCount + 1))}
              t={t}
            />
          )}

          <div className="mt-5 rounded-lg border border-hairline" style={{ background: "#F9FAFB", padding: "14px 18px" }}>
            <div className="flex items-start gap-3">
              <div className="mt-0.5"><CheckIcon /></div>
              <div>
                <div className="mb-0.5 text-small font-bold text-ink">{t("cart.pricesIn", { currency: country.currency })}</div>
                <div className="text-tiny text-sub leading-relaxed">{t("cart.taxAndPromoNote", { tax: getTaxLabel(), months: getPromoMonths() })}</div>
              </div>
            </div>
          </div>
        </div>

        <OrderSummary
          planName={planName}
          planRegular={planRegular}
          hasPayroll={!!payroll}
          payrollName={payrollName}
          payrollRegularBase={payrollRegularBase}
          perEmployee={payroll ? payroll.perEmployee : 0}
          perEmpCharge={perEmpCharge}
          employeeCount={employeeCount}
          subtotalRegular={subtotalRegular}
          savings={savings}
          gst={gst}
          total={total}
          currency={country.currency}
          language={i18n.language}
          t={t}
          onContinue={goCheckout}
        />

      </div>

      <MarketingFooter />
    </div>
  );
}
